import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Table,
  Button,
  Input,
  Select,
  DatePicker,
  Space,
  Card,
  Row,
  Col,
  Dropdown,
  Modal,
  Typography,
} from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  MoreOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  SendOutlined,
  FilePdfOutlined,
  SwapOutlined,
  ReloadOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import moment from "moment";
import {
  getAllQuotations,
  deleteQuotation,
  sendQuotation,
  convertToOrder,
  createRevision,
  getQuotationPDF,
} from "../../api/quotationApi";
import QuotationStatusBadge from "./QuotationStatusBadge";
import PageTitle from "../page-header/PageHeader";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title } = Typography;

const QuotationList = () => {
  const navigate = useNavigate();
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    status: undefined,
    search: "",
    start_date: undefined,
    end_date: undefined,
  });

  // Fetch quotations
  const fetchQuotations = useCallback(
    async (page = 1, pageSize = 10) => {
      setLoading(true);
      try {
        const params = {
          page,
          count: pageSize,
        };

        if (filters.status) params.status = filters.status;
        if (filters.search) params.search = filters.search;
        if (filters.start_date) params.start_date = filters.start_date;
        if (filters.end_date) params.end_date = filters.end_date;

        const response = await getAllQuotations(params);
        setQuotations(response.data || []);
        setPagination({
          current: response.page || page,
          pageSize: response.limit || pageSize,
          total: response.total || 0,
        });
      } catch (error) {
        console.log(error.message);
        toast.error("Failed to fetch quotations");
      } finally {
        setLoading(false);
      }
    },
    [filters]
  );

  useEffect(() => {
    fetchQuotations();
  }, [fetchQuotations]);

  // Handle table change
  const handleTableChange = (paginationInfo) => {
    fetchQuotations(paginationInfo.current, paginationInfo.pageSize);
  };

  // Handle delete
  const handleDelete = (id) => {
    Modal.confirm({
      title: "Delete Quotation",
      content: "Are you sure you want to delete this quotation?",
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        try {
          await deleteQuotation(id);
          toast.success("Quotation deleted successfully");
          fetchQuotations(pagination.current, pagination.pageSize);
        } catch (error) {
          toast.error("Failed to delete quotation");
        }
      },
    });
  };

  // Handle send
  const handleSend = async (id) => {
    try {
      await sendQuotation(id);
      toast.success("Quotation sent successfully");
      fetchQuotations(pagination.current, pagination.pageSize);
    } catch (error) {
      toast.error("Failed to send quotation");
    }
  };

  // Handle convert to order
  const handleConvert = (id) => {
    Modal.confirm({
      title: "Convert to Order",
      content: "Convert this quotation to a confirmed order?",
      okText: "Convert",
      onOk: async () => {
        try {
          const result = await convertToOrder(id);
          toast.success("Order created successfully");
          fetchQuotations(pagination.current, pagination.pageSize);
        } catch (error) {
          toast.error("Failed to convert quotation");
        }
      },
    });
  };

  // Handle revision
  const handleRevision = async (id) => {
    try {
      const result = await createRevision(id);
      toast.success("Revision created successfully");
      navigate(`/quotation/${result.id}/edit`);
    } catch (error) {
      toast.error("Failed to create revision");
    }
  };

  // Handle PDF download
  const handleDownloadPDF = async (id, quotationNumber) => {
    try {
      const blob = await getQuotationPDF(id);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${quotationNumber || "quotation"}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  // Handle date range change
  const handleDateChange = (dates) => {
    if (dates) {
      setFilters({
        ...filters,
        start_date: dates[0].format("YYYY-MM-DD"),
        end_date: dates[1].format("YYYY-MM-DD"),
      });
    } else {
      setFilters({
        ...filters,
        start_date: undefined,
        end_date: undefined,
      });
    }
  };

  // Action menu items
  const getActionMenuItems = (record) => ({
    items: [
      {
        key: "view",
        icon: <EyeOutlined />,
        label: "View Details",
        onClick: () => navigate(`/quotation/${record.id}`),
      },
      {
        key: "edit",
        icon: <EditOutlined />,
        label: "Edit",
        onClick: () => navigate(`/quotation/${record.id}/edit`),
        disabled: record.status !== "draft",
      },
      {
        key: "pdf",
        icon: <FilePdfOutlined />,
        label: "Download PDF",
        onClick: () =>
          handleDownloadPDF(record.id, record.quotation_number),
      },
      { type: "divider" },
      {
        key: "send",
        icon: <SendOutlined />,
        label: "Send to Customer",
        onClick: () => handleSend(record.id),
        disabled: record.status !== "draft",
      },
      {
        key: "revision",
        icon: <CopyOutlined />,
        label: "Create Revision",
        onClick: () => handleRevision(record.id),
        disabled: !["sent", "viewed", "rejected"].includes(record.status),
      },
      {
        key: "convert",
        icon: <SwapOutlined />,
        label: "Convert to Order",
        onClick: () => handleConvert(record.id),
        disabled: record.status !== "accepted",
      },
      { type: "divider" },
      {
        key: "delete",
        icon: <DeleteOutlined />,
        label: "Delete",
        danger: true,
        onClick: () => handleDelete(record.id),
        disabled: !["draft", "rejected", "expired"].includes(record.status),
      },
    ],
  });

  // Table columns
  const columns = [
    {
      title: "Quotation #",
      dataIndex: "quotation_number",
      key: "quotation_number",
      render: (text, record) => (
        <Link to={`/quotation/${record.id}`}>
          <strong>{text}</strong>
        </Link>
      ),
    },
    {
      title: "Customer",
      dataIndex: "customer_name",
      key: "customer_name",
      render: (text, record) => text || record.customer?.name || "-",
    },
    {
      title: "Items",
      dataIndex: "items",
      key: "items_count",
      render: (items) => items?.length || 0,
      align: "center",
    },
    {
      title: "Total Amount",
      dataIndex: "total_amount",
      key: "total_amount",
      render: (amount) =>
        `₹${Number(amount || 0).toLocaleString("en-IN", {
          minimumFractionDigits: 2,
        })}`,
      align: "right",
    },
    {
      title: "Valid Until",
      dataIndex: "valid_until",
      key: "valid_until",
      render: (date) => (date ? moment(date).format("DD MMM YYYY") : "-"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <QuotationStatusBadge status={status} />,
    },
    {
      title: "Version",
      dataIndex: "version",
      key: "version",
      render: (v) => `v${v || 1}`,
      align: "center",
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      render: (date) => (date ? moment(date).format("DD MMM YYYY") : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      fixed: "right",
      render: (_, record) => (
        <Dropdown menu={getActionMenuItems(record)} trigger={["click"]}>
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <>
      <PageTitle title="Back" />
      <Row className="mr-top" justify="space-between" gutter={[0, 30]}>
        <Col
          xs={24}
          sm={24}
          md={24}
          lg={24}
          xl={24}
          className="rounded column-design"
        >
          <Card bordered={false}>
            <div
              className="d-flex justify-content-between"
              style={{ marginBottom: 16 }}
            >
              <Title level={4} className="m-0">
                Quotations
              </Title>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate("/quotation/new")}
              >
                New Quotation
              </Button>
            </div>

            {/* Filters */}
            <div style={{ marginBottom: 16 }}>
              <Space wrap>
                <Input
                  placeholder="Search by quotation #"
                  prefix={<SearchOutlined />}
                  value={filters.search}
                  onChange={(e) =>
                    setFilters({ ...filters, search: e.target.value })
                  }
                  style={{ width: 220 }}
                  allowClear
                />
                <Select
                  placeholder="Status"
                  value={filters.status}
                  onChange={(value) =>
                    setFilters({ ...filters, status: value })
                  }
                  style={{ width: 150 }}
                  allowClear
                >
                  <Option value="draft">Draft</Option>
                  <Option value="sent">Sent</Option>
                  <Option value="viewed">Viewed</Option>
                  <Option value="accepted">Accepted</Option>
                  <Option value="rejected">Rejected</Option>
                  <Option value="expired">Expired</Option>
                  <Option value="converted">Converted</Option>
                  <Option value="revised">Revised</Option>
                </Select>
                <RangePicker onChange={handleDateChange} />
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    setFilters({
                      status: undefined,
                      search: "",
                      start_date: undefined,
                      end_date: undefined,
                    });
                  }}
                >
                  Clear
                </Button>
              </Space>
            </div>

            {/* Table */}
            <Table
              columns={columns}
              dataSource={quotations}
              rowKey="id"
              loading={loading}
              pagination={{
                ...pagination,
                showSizeChanger: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} quotations`,
              }}
              onChange={handleTableChange}
              scroll={{ x: 1100 }}
            />
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default QuotationList;
